import { useEffect, useState, useCallback } from "react";
import "@/App.css";
import { motion, AnimatePresence } from "framer-motion";
import { MapPin, Heart, X, Phone, Mail, Globe } from "lucide-react";

// Animation constants (extracted to prevent re-creation on every render)
const ANIMATION_Y_OFFSET = 30;
const NAV_SECTIONS = ["home", "about", "menu", "breakfast", "lunch", "wine", "location"];

const FADE_IN_UP = {
  initial: { opacity: 0, y: ANIMATION_Y_OFFSET },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true },
};

const FADE_IN_X_LEFT = {
  initial: { opacity: 0, x: -50 },
  animate: { opacity: 1, x: 0 },
};

const NAV_INITIAL = { y: -100 };
const NAV_ANIMATE = { y: 0 };
const NAV_TRANSITION = { duration: 0.6 };

const HOVER_SCALE = { scale: 1.02, x: 4 };
const MODAL_BACKDROP_INITIAL = { opacity: 0 };
const MODAL_BACKDROP_ANIMATE = { opacity: 1 };
const MODAL_BACKDROP_EXIT = { opacity: 0 };
const MODAL_CONTENT_INITIAL = { scale: 0.9, opacity: 0 };
const MODAL_CONTENT_ANIMATE = { scale: 1, opacity: 1 };
const MODAL_CONTENT_EXIT = { scale: 0.9, opacity: 0 };
const MODAL_TRANSITION = { duration: 0.3 };
const VIEWPORT_ONCE = { once: true };
const TRANSITION_06 = { duration: 0.6 };
const TRANSITION_07 = { duration: 0.7 };
const TRANSITION_08 = { duration: 0.8 };
const HERO_DELAYED_TRANSITION = { duration: 0.8, delay: 0.2 };
const FOOTER_NOTE_TRANSITION = { duration: 0.6, delay: 0.4 };
const OPACITY_FADE_INITIAL = { opacity: 0 };
const OPACITY_FADE_IN_VIEW = { opacity: 1 };

// ============================================================================
// MENU DATA - Hoisted to module level for performance and cleaner component
// ============================================================================

const itemDescriptions = {
  "Biscoff Macchiato Latte": "Bold espresso, creamy milk, swirls of biscoff, topped with caramel drizzle and biscuit crumble",
  "Cookies & Cream Latte": "Two shots of iced espresso, dark chocolate syrup, vanilla cold foam, and a satisfying oreo crumb top",
  "Mocha Mudslide Latte": "Decadent blend of rich espresso, dark chocolate, and velvety Irish Cream syrup, topped with cold foam and chocolate drizzle",
  "French Vanilla Frappe": "Rich espresso, creamy milk and ice, sweet French vanilla syrup, topped with whipped cream",
  "White Choco Frappe": "White chocolate sauce, espresso, milk and ice topped with whipped cream",
  "Hazelnut Frappe": "Espresso, hazelnut syrup, milk and ice, topped with whipped cream and a drizzle of hazelnut chocolate sauce",
  "Spanish Latte": "Rich espresso paired with sweetened condensed milk over ice for a smooth, creamy indulgence",
  "Mocha Latte": "Bold espresso, rich chocolate, and creamy milk over ice — pure chocolaty bliss",
  "White Mocha Latte": "Smooth white chocolate sauce, espresso, and milk over ice — sweet and velvety",
  "Biscoff Latte": "Iced espresso swirled with biscoff cookie butter and creamy milk for a caramelized treat",
  "Honey Latte": "Espresso and milk sweetened with golden honey, served over ice for natural sweetness",
  "French Vanilla Latte": "Classic espresso and milk infused with sweet French vanilla, served chilled over ice",
  "Hazelnut Latte": "Iced espresso, milk, and nutty hazelnut syrup for a smooth, aromatic finish",
  "Banana Nut": "Bananas, Whole Milk, Almond Butter, Walnuts, Ground Flaxseed Meal, Honey, Cinnamon",
  "Emerald Glow": "Spinach, Pineapple, Whole Milk or Coconut Water, Spirulina Powder, Ground Flaxseed Meal",
  "Tropic Mango": "Mango, Whole Milk, Coco Lopez, Ground Flaxseed Meal, Maraschino Cherry Syrup, Maraschino Cherries",
  "Cosmic Grape": "Grape Juice, Berries, Ground Flaxseed Meal",
  "Zesty Peach": "Refreshing and crisp, peach vibes with a citrus kick",
  "Honey Mint Haze": "Revitalize your body with cool mint, golden honey, and a green tea base",
  "Passion Limeade": "Passionately fresh, a vibrant blend of sweet and tangy",
  "Pineapple Zinger": "Sharp pineapple sweetness, balances spicy ginger",
  "Crimson Strawberry": "Bright strawberry meets floral hibiscus for a refreshing playful sip",
  "Vanilla Chai": "Warm spices, smooth vanilla, pure relief",
  "Golden Milk": "Healing in a cup",
  "Lemongrass": "Freshly steeped comfort",
  "Chamomile": "Quiet your mind, one sip at a time",
  "Peppermint": "Sheer minty tranquility"
};

const getItemDescription = (itemName, category) => {
  if (itemDescriptions[itemName]) {
    return itemDescriptions[itemName];
  }
  
  // Default descriptions by category
  if (category === "Fresh Coffee") return "Our premium coffee is carefully sourced and expertly roasted to bring out the perfect balance of flavor and aroma.";
  if (category === "Iced") return "Refreshing iced espresso drinks crafted with premium ingredients and topped to perfection.";
  if (category === "Hot Tea") return "Handpicked tea leaves steeped to perfection, offering a soothing and aromatic experience.";
  if (category === "Iced Tea") return "Refreshing and crisp, our iced teas are the perfect companion for a warm day.";
  if (category === "Smoothies") return "Fresh fruit smoothies blended to creamy perfection, packed with nutrients and flavor.";
  if (category === "Juices") return "100% fresh-pressed juices bursting with natural sweetness and vitality.";
  if (category === "Non-Dairy") return "Customize your beverage with our selection of non-dairy alternatives.";
  return "";
};

const menuData = {
  "Fresh Coffee": [
    { name: "Espresso", sm: "dbl $3", lg: "quad $4" },
    { name: "Cappuccino", sm: "$4", lg: "$6" },
    { name: "Americano", sm: "$4", lg: "$6" },
    { name: "Classic Latte", sm: "$4", lg: "$6" },
    { name: "Macchiato", sm: "$5", lg: "$6" },
    { name: "Affogato", sm: "$5", lg: "$6" },
    { name: "Mocha", sm: "$4", lg: "$6" },
    { name: "Hot Chocolate", sm: "$4", lg: "$6" },
  ],
  "Iced": [
    { name: "Spanish Latte", sm: "$4", lg: "$6" },
    { name: "Mocha Latte", sm: "$4", lg: "$6" },
    { name: "White Mocha Latte", sm: "$5", lg: "$7" },
    { name: "Biscoff Latte", sm: "$5", lg: "$6" },
    { name: "Honey Latte", sm: "$5", lg: "$7" },
    { name: "French Vanilla Latte", sm: "$4", lg: "$6" },
    { name: "Hazelnut Latte", sm: "$5", lg: "$7" },
  ],
  "Hot Tea": [
    { name: "Vanilla Chai", sm: "$2", lg: "$3" },
    { name: "Lemongrass", sm: "$2", lg: "$3" },
    { name: "Chamomile", sm: "$2", lg: "$3" },
    { name: "Peppermint", sm: "$2", lg: "$3" },
  ],
  "Smoothies": [
    { name: "Banana Nut", price: "$8.00", description: "Bananas, Whole Milk, Almond Butter, Walnuts, Ground Flaxseed Meal, Honey, Cinnamon" },
    { name: "Emerald Glow", price: "$8.00", description: "Spinach, Pineapple, Whole Milk or Coconut Water, Spirulina Powder, Ground Flaxseed Meal" },
    { name: "Tropic Mango", price: "$8.00", description: "Mango, Whole Milk, Coco Lopez, Ground Flaxseed Meal, Maraschino Cherry Syrup, Maraschino Cherries" },
    { name: "Cosmic Grape", price: "$8.00", description: "Grape Juice, Berries, Ground Flaxseed Meal" },
  ],
  "Juices": [
    { name: "Passionfruit", sm: "$5", lg: "$7" },
    { name: "Lemonade", sm: "$5", lg: "$7" },
    { name: "Tamarind", sm: "$5", lg: "$7" },
    { name: "Soursop", sm: "$5", lg: "$7" },
  ],
};

const breakfastMenu = {
  "Grains & Griddle": [
    { 
      name: "Steel Cut Oatmeal / Cream of Wheat", 
      price: "$4 / $6",
      description: "Cup $4 or Bowl $6, served with brown sugar, honey or cinnamon"
    },
    { 
      name: "Granola", 
      price: "$11",
      description: "Greek yogurt served w/granola & mixed berries"
    },
    { 
      name: "Seasonal Fruit Bowl", 
      price: "$14",
      description: "Fresh seasonal fruits with whipped cream and honey on the side"
    },
  ],
  "Pancakes & Waffles": [
    { 
      name: "Buttermilk Pancakes", 
      price: "$7 / $11",
      description: "2 stack $7 or 4 stack $11, served with berries, powdered sugar & maple syrup (Add blueberries, chocolate chips, banana +$2)"
    },
    { 
      name: "Waffles", 
      price: "$10",
      description: "Served with mixed berries (Add Nutella, blueberries, chocolate chip, banana +$2)"
    },
    { 
      name: "Bushwhacker French Toast", 
      price: "$10",
      description: "Served with mixed seasonal berries, honey, whipped cream"
    },
  ],
  "Omelettes": [
    { 
      name: "Cheese Omelette", 
      price: "$13",
      description: "Melted cheese, three eggs, served with breakfast potatoes & toast"
    },
    { 
      name: "Egg White Omelette", 
      price: "$14",
      description: "With cherry tomatoes, bell peppers, served with breakfast potatoes & toast"
    },
    { 
      name: "Ham & Cheese Omelette", 
      price: "$15",
      description: "Eggs, ham, cheddar cheese, served with breakfast potatoes & toast"
    },
    { 
      name: "Vegetarian Omelette", 
      price: "$15",
      description: "Eggs, peppers, onions, mushrooms & tomatoes topped with Swiss cheese, served with breakfast potatoes"
    },
  ],
  "Classics & Specialties": [
    { 
      name: "All American Classic Breakfast", 
      price: "$10",
      description: "Two eggs any style, served with breakfast potatoes & toast (Add bacon, sausage, or ham +$4)"
    },
    { 
      name: "Steak & Eggs", 
      price: "$22",
      description: "Grilled steak served with two fried eggs, breakfast potatoes & toast"
    },
    { 
      name: "Eggs Benedict", 
      price: "$17",
      description: "Poached eggs over Canadian bacon, toasted English muffin, topped with hollandaise sauce, served with breakfast potatoes"
    },
    { 
      name: "Island Saltfish", 
      price: "$20",
      description: "Warm saltfish mix served with okra & spinach callaloo, hard boiled egg, French bread"
    },
  ],
  "Sandwiches & Toasts": [
    { 
      name: "Breakfast Sandwich", 
      price: "$14",
      description: "Fried eggs, American cheese, smoky bacon, bagel served with breakfast potatoes"
    },
    { 
      name: "Classic Bagel", 
      price: "$8",
      description: "Choose from plain or everything (Add cream cheese or peanut butter +$2)"
    },
    { 
      name: "Smoked Salmon Bagel", 
      price: "$19",
      description: "Bagel served with smoked salmon, pickled red onions, cream cheese, tomato & capers"
    },
    { 
      name: "Avocado Toast", 
      price: "$16",
      description: "Fresh crushed avocados with pickled red onion, cherry tomatoes, baby arugula on whole grain sourdough bread, served with breakfast potatoes (Add eggs +$3)"
    },
    { 
      name: "Egg Quesadilla", 
      price: "$14",
      description: "Egg whites or whole eggs, turkey bacon, bell peppers, onions, mozzarella, tomato salsa, sour cream, guacamole"
    },
  ],
  "Side Orders & Beverages": [
    { name: "One Boiled Egg", price: "$3" },
    { name: "Ham, Bacon, or Sausage", price: "$7" },
    { name: "Fruit Bowl", price: "$7" },
    { name: "Hash Brown", price: "$4" },
    { name: "Breakfast Potatoes", price: "$4" },
    { name: "One Pancake", price: "$4" },
    { name: "Toast", price: "$3" },
    { name: "Coffee", price: "$5" },
    { name: "Herbal Tea", price: "$5" },
    { name: "Orange Juice", price: "$5" },
    { name: "Apple Juice", price: "$5" },
    { name: "Pineapple Juice", price: "$5" },
    { name: "Cranberry Juice", price: "$5" },
  ],
};

const lunchMenu = {
  "Appetizers": [
    { name: "Conch Fritters", price: "$16", description: "Deep fried battered conch fritters, Jerk Mango Chutney" },
    { name: "Island Coconut Shrimp", price: "$16", description: "Hot Honey Sauce" },
    { name: "Caribbean Conch Ceviche", price: "$19", description: "Served with Tortilla Chips" },
    { name: "Chips & Salsa", price: "$10", description: "Served with Salsa" },
    { name: "Fried Mozzarella Sticks", price: "$12", description: "Served with Marinara Sauce or Ranch" },
    { name: "Loaded Nachos", price: "$14", description: "Yellow chips topped with house cheese sauce, salsa, sour cream, guacamole, sliced jalapeños, and cilantro (Add Pulled Jerk Chicken +$9, Black Bean +$8, Ground Beef +$10)" },
    { name: "Chicken Tenders & Fries (3 Tenders)", price: "$15", description: "Honey Mustard, Ranch, BBQ, Hot Honey Sauce" },
      { name: "Chicken Tenders & Fries (6 Tenders)", price: "$20", description: "Honey Mustard, Ranch, BBQ, Hot Honey Sauce" },
      { name: "St. John's Wings (Half Dozen)", price: "$12", description: "Deep fried wings with choice of Korean Style BBQ Sauce, Buffalo sauce, Citrus Jerk sauce, Hot Honey, carrots & celery sticks, Ranch or Blue Cheese" },
      { name: "St. John's Wings (One Dozen)", price: "$22", description: "Deep fried wings with choice of Korean Style BBQ Sauce, Buffalo sauce, Citrus Jerk sauce, Hot Honey, carrots & celery sticks, Ranch or Blue Cheese" },
    { name: "Hummus Brochette", price: "$16", description: "Hummus on pita bread topped with tomato, olives, cucumber and basil, tossed in olive oil and balsamic vinegar" },
    { name: "Quesadilla", price: "$15", description: "10\" tortilla wraps stuffed with Monterey Jack cheese, served with salsa, guacamole, and sour cream" },
  ],
  "Salads": [
    { name: "Chef Caesar", price: "$17", description: "Local romaine hearts, shaved parmesan cheese, herb croutons, caesar dressing" },
    { name: "House Salad", price: "$18", description: "Mixed greens, red onions, shaved carrots, cucumbers, cherry tomatoes, olives, feta cheese (Add Grilled Chicken +$10, Shrimp +$12, Steak +$16, Salmon +$14, Mahi +$11)" },
  ],
  "Sandwiches": [
    { name: "Cheeseburger", price: "$18", description: "8oz Angus patty, bun, lettuce, tomato, house pickles, melted cheese" },
    { name: "Philly Cheese Steak", price: "$19", description: "Tender Angus steak, French bread, red & green bell peppers, sautéed onions, melted provolone cheese" },
    { name: "Jerk Chicken Cheese Steak", price: "$18", description: "Jerk chicken, French bread, red & green bell peppers, sautéed onions, melted provolone cheese" },
    { name: "Grilled Vegetable Cheese Steak", price: "$16", description: "Grilled mushrooms, French bread, red & green peppers, sautéed onions, melted provolone cheese" },
    { name: "Blackened Salmon", price: "$20", description: "Blackened 7oz salmon fillet, toasted bun, tartar sauce, lettuce, tomato, house pickles" },
    { name: "Jerk Mahi", price: "$19", description: "Grilled 7oz mahi on toasted bun, jerk aioli" },
      { name: "Fish & Chips", price: "$19", description: "Deep fried battered fish served with French fries, tartar sauce and lemon wedge" },
    { name: "Beyond Veggie Burger", price: "$18", description: "Homestyle 8oz veggie patty, brioche bun, sautéed onions, lettuce, tomato, house pickles, melted Swiss cheese" },
  ],
  "Le Poshe Pizza": [
    { name: "Cheese Pizza", price: "$17", description: "10\" pizza topped with marinara and mozzarella cheese" },
    { name: "Pepperoni Pizza", price: "$18", description: "10\" pizza topped with marinara, mozzarella cheese, sliced pepperoni" },
    { name: "Buffalo Chicken Pizza", price: "$19", description: "10\" pizza topped with marinara, buffalo chicken, mozzarella cheese, green onions; served with a side of ranch dressing" },
    { name: "Spinach & Ricotta Cheese Pizza", price: "$19", description: "10\" pizza topped with marinara, mozzarella cheese, ricotta cheese, sliced tomato, shredded spinach" },
    { name: "Veggie Pizza", price: "$18", description: "10\" pizza topped with marinara, mozzarella cheese, olives, mushrooms, peppers, onions" },
  ],
  "Desserts": [
    { name: "*Ask about daily pastry selection", price: "" },
  ],
};

const wineMenu = {
  "Bubbles": [
    { name: "San Martino, Prosecco", glass: "$10", bottle: "$31" },
    { name: "Freixent, Sparkling", glass: "$10", bottle: "$31" },
    { name: "Berlucchi 61 Franciacota EXTRA Brut", glass: "", bottle: "$80" },
    { name: "Berlucchi Franciacota Rosé", glass: "", bottle: "$90" },
    { name: "Brut Reserve, Bilicart (FRA)", glass: "", bottle: "$100" },
  ],
  "Rosé": [
    { name: "La Vieille Ferme, Rosé", glass: "$9", bottle: "$30" },
    { name: "Cote de Rose, Gerard Betrand (FRA)", glass: "", bottle: "$59" },
  ],
  "Sweet Wines": [
    { name: "Luna, Moscato", glass: "$12", bottle: "$42" },
    { name: "Sangue di Guida \"Terra\" Sweet Red", glass: "$14", bottle: "$49" },
  ],
  "Whites": [
    { name: "La Vieille Ferme, Blanc", glass: "$9", bottle: "$30" },
    { name: "Pinot Grigio, Vegnetti", glass: "$10", bottle: "$35" },
    { name: "Chardonnay Grand Sasso", glass: "$14", bottle: "$49" },
    { name: "Chablis Ellevin (FRA)", glass: "$20", bottle: "$70" },
  ],
  "Reds": [
    { name: "La Vieille Ferme, Rouge", glass: "$9", bottle: "$30" },
    { name: "Red Blend \"Appassimento\"", glass: "$16", bottle: "$30" },
    { name: "Pinot Noir, Paradosso", glass: "$10", bottle: "$35" },
    { name: "*Sangiovese/Cabernet Sauvignon, Don Camillo", glass: "", bottle: "$58" },
  ],
};

const categoryImages = {
  "Fresh Coffee": "https://images.pexels.com/photos/4264049/pexels-photo-4264049.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  "Iced": "https://images.pexels.com/photos/11512983/pexels-photo-11512983.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  "Hot Tea": "https://images.pexels.com/photos/734983/pexels-photo-734983.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  "Smoothies": "https://images.pexels.com/photos/33640921/pexels-photo-33640921.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  "Juices": "https://images.pexels.com/photos/17612801/pexels-photo-17612801.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
};

// ============================================================================
// SUB-COMPONENTS
// ============================================================================

const ItemModal = ({ item, onClose, toggleFavorite, isFavorite }) => {
  if (!item) return null;
  const favorited = isFavorite(item.category, item.name);

  return (
    <motion.div
      initial={MODAL_BACKDROP_INITIAL}
      animate={MODAL_BACKDROP_ANIMATE}
      exit={MODAL_BACKDROP_EXIT}
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm"
      onClick={onClose}
      data-testid="item-modal"
    >
      <motion.div
        initial={MODAL_CONTENT_INITIAL}
        animate={MODAL_CONTENT_ANIMATE}
        exit={MODAL_CONTENT_EXIT}
        transition={MODAL_TRANSITION}
        className="bg-gradient-to-br from-[#2A1810] to-[#0a0a0a] border border-[#3D2817]/60 rounded-lg p-8 max-w-md w-full relative"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-[#9ca3af] hover:text-[#e5e5e5] transition-colors"
          data-testid="close-modal"
        >
          <X className="w-6 h-6" />
        </button>

        <div className="mb-6">
          <p className="text-[#9ca3af] text-sm uppercase tracking-wide mb-2">
            {item.category}
          </p>
          <h3 className="heading-font text-3xl font-semibold text-[#e5e5e5] mb-4">
            {item.name}
          </h3>

          {item.sm && item.lg ? (
            <div className="flex gap-6 mb-6">
              <div className="flex-1">
                <p className="text-[#9ca3af] text-xs uppercase tracking-wide mb-1">Small</p>
                <p className="text-2xl font-semibold text-[#e5e5e5]">{item.sm}</p>
              </div>
              <div className="flex-1">
                <p className="text-[#9ca3af] text-xs uppercase tracking-wide mb-1">Large</p>
                <p className="text-2xl font-semibold text-[#e5e5e5]">{item.lg}</p>
              </div>
            </div>
          ) : (
            <p className="text-3xl font-semibold text-[#e5e5e5] mb-6">{item.price}</p>
          )}

          <p className="text-[#a1a1a1] leading-relaxed mb-6">
            {getItemDescription(item.name, item.category)}
          </p>
        </div>

        <button
          onClick={() => toggleFavorite(item.category, item.name)}
          className="w-full bg-[#3D2817]/60 hover:bg-[#3D2817]/90 text-[#e5e5e5] py-3 rounded-full flex items-center justify-center gap-2 transition-colors duration-300"
          data-testid="modal-favorite-button"
        >
          <Heart
            className={`w-5 h-5 ${favorited ? 'fill-[#e5e5e5]' : ''}`}
          />
          {favorited ? 'Remove from Favorites' : 'Add to Favorites'}
        </button>
      </motion.div>
    </motion.div>
  );
};

const CARD_TRANSITION_BASE = { duration: 0.6 };
const getCardTransition = (index) => ({ ...CARD_TRANSITION_BASE, delay: index * 0.1 });

const BeverageMenuCard = ({ category, items, categoryIndex, onSelectItem, toggleFavorite, isFavorite }) => {
  const image = categoryImages[category];
  const hasSizePricing = items[0] && items[0].sm;
  const testIdSafe = (name) => name.toLowerCase().replace(/\s+/g, '-');

  let sizeIndicator;
  if (category === "Fresh Coffee" || category === "Iced") {
    sizeIndicator = (
      <p className="text-xs text-[#9ca3af] uppercase tracking-wide mb-6 font-medium">
        12 - 16 oz <span className="text-[#9ca3af] normal-case tracking-normal">(Non-Dairy: Almond, Coconut, Oat, Soy +$1)</span>
      </p>
    );
  } else if (category === "Juices") {
    sizeIndicator = <p className="text-xs text-[#9ca3af] uppercase tracking-wide mb-6 font-medium">12 - 16 oz</p>;
  } else if (category === "Hot Tea") {
    sizeIndicator = <p className="text-xs text-[#9ca3af] uppercase tracking-wide mb-6 font-medium">Sm - Lg</p>;
  } else {
    sizeIndicator = <div className="mb-6"></div>;
  }

  return (
    <motion.div
      initial={FADE_IN_UP.initial}
      whileInView={FADE_IN_UP.whileInView}
      viewport={VIEWPORT_ONCE}
      transition={getCardTransition(categoryIndex)}
      className="border border-[#3D2817]/60 rounded-sm p-8 menu-item-transition bg-black/30 backdrop-blur-sm"
      data-testid={`menu-category-${testIdSafe(category)}`}
    >
      {image && (
        <div className="mb-6">
          {Array.isArray(image) ? (
            <div className="grid grid-cols-2 gap-2">
              {image.map((imgUrl, idx) => (
                <div key={imgUrl} className="h-40 rounded-sm overflow-hidden">
                  <img src={imgUrl} alt={`${category} ${idx + 1}`} className="w-full h-full object-cover" />
                </div>
              ))}
            </div>
          ) : (
            <div className="h-48 rounded-sm overflow-hidden">
              <img src={image} alt={category} className="w-full h-full object-cover" />
            </div>
          )}
        </div>
      )}
      <h4 className="heading-font text-2xl font-semibold text-[#e5e5e5] mb-2 tracking-tight">
        {category}
      </h4>
      {sizeIndicator}
      <div className="space-y-3">
        {items.map((item) => {
          const favClass = isFavorite(category, item.name) ? 'fill-[#9ca3af] text-[#9ca3af]' : 'text-[#9ca3af]';
          const testId = testIdSafe(item.name);
          const rowClass = hasSizePricing
            ? "grid grid-cols-[1fr,auto,auto,auto] gap-3 items-center p-2 rounded-sm hover:bg-[#2A1810]/60 transition-colors duration-200 cursor-pointer group"
            : "flex justify-between items-center p-2 rounded-sm hover:bg-[#2A1810]/60 transition-colors duration-200 cursor-pointer group";
          return (
            <motion.div
              key={item.name}
              whileHover={HOVER_SCALE}
              className={rowClass}
              data-testid={`menu-item-${testId}`}
              onClick={() => onSelectItem({ ...item, category })}
            >
              {hasSizePricing ? (
                <>
                  <span className="text-[#d1d5db] font-normal group-hover:text-[#e5e5e5]">{item.name}</span>
                  <span className="text-[#9ca3af] font-medium text-right w-16">{item.sm}</span>
                  <span className="text-[#9ca3af] font-medium text-right w-16">{item.lg}</span>
                </>
              ) : (
                <>
                  <div className="flex-1 min-w-0">
                    <span className="block text-[#d1d5db] font-normal group-hover:text-[#e5e5e5]">{item.name}</span>
                    {item.description && (
                      <p
                        className="text-xs text-[#a1a1a1] font-light leading-relaxed mt-1"
                        data-testid={`menu-item-description-${testId}`}
                      >
                        {item.description}
                      </p>
                    )}
                  </div>
                  <span className="text-[#9ca3af] font-medium ml-2 shrink-0">{item.price}</span>
                </>
              )}
              <button
                onClick={(e) => { e.stopPropagation(); toggleFavorite(category, item.name); }}
                className={`opacity-0 group-hover:opacity-100 transition-opacity${hasSizePricing ? '' : ' ml-2'}`}
                data-testid={`favorite-${testId}`}
              >
                <Heart className={`w-4 h-4 ${favClass}`} />
              </button>
            </motion.div>
          );
        })}
      </div>
    </motion.div>
  );
};

// ============================================================================
// PAGE SECTIONS — extracted for readability and to reduce App() complexity
// ============================================================================

const HeroSection = () => (
  <section id="home" className="pt-32 pb-16 px-6" data-testid="hero-section">
    <div className="max-w-7xl mx-auto">
      <div className="grid md:grid-cols-2 gap-12 items-center py-16">
        <motion.div
          initial={FADE_IN_X_LEFT.initial}
          animate={FADE_IN_X_LEFT.animate}
          transition={TRANSITION_08}
        >
          <h2 className="heading-font text-5xl md:text-6xl lg:text-7xl font-semibold text-[#e5e5e5] tracking-tight leading-tight mb-6">
            Crafted With Passion, Served With Love
          </h2>
          <p className="text-lg text-[#a1a1a1] leading-relaxed mb-8 font-light">
            Experience the finest selection of coffee, tea, and refreshing beverages
            in the heart of St. Thomas.
          </p>
          <a
            href="#menu"
            data-testid="view-menu-button"
            className="inline-block bg-[#A47551] text-[#1a0f0a] px-8 py-3 rounded-full text-sm uppercase tracking-wide font-medium hover:bg-[#C99072] transition-colors duration-300"
          >
            View Our Menu
          </a>
        </motion.div>
      </div>
    </div>
  </section>
);

const AboutSection = () => (
  <section id="about" className="py-24 px-6" data-testid="about-section">
    <div className="max-w-7xl mx-auto">
      <div className="max-w-4xl mx-auto">
        <motion.div
          initial={FADE_IN_UP.initial}
          whileInView={FADE_IN_UP.whileInView}
          viewport={VIEWPORT_ONCE}
          transition={TRANSITION_07}
        >
          <h3 className="heading-font text-4xl md:text-5xl font-semibold text-[#e5e5e5] tracking-tight mb-6">
            Where Every Cup Tells a Story
          </h3>
          <p className="text-lg text-[#a1a1a1] leading-relaxed mb-6 font-light">
            At Le Poshe Cafe, every visit is a journey through flavor. From our handcrafted coffees,
            teas, and refreshing smoothies to our cold-pressed island juices and curated international
            wine selection, every sip is crafted with intention and the finest ingredients.
          </p>
          <p className="text-lg text-[#a1a1a1] leading-relaxed mb-6 font-light">
            Our kitchen brings the same passion to the plate — serving hearty breakfast classics,
            Caribbean-inspired lunch favorites, artisan pizzas, fresh salads, and an ever-changing
            selection of in-house baked pastries that pair perfectly with your morning brew.
          </p>
          <p className="text-lg text-[#a1a1a1] leading-relaxed font-light">
            Whether you&apos;re starting your day, sharing a midday meal, or unwinding over a glass of wine,
            Le Poshe Cafe is where every cup — and every plate — tells a story worth savoring.
          </p>
        </motion.div>
      </div>
    </div>
  </section>
);

const SimpleMenuCard = ({ testIdPrefix, category, items, categoryIndex }) => {
  const slug = category.toLowerCase().replace(/[^a-z0-9]+/g, '-');
  return (
    <motion.div
      initial={FADE_IN_UP.initial}
      whileInView={FADE_IN_UP.whileInView}
      viewport={VIEWPORT_ONCE}
      transition={getCardTransition(categoryIndex)}
      className="border border-[#3D2817]/60 rounded-sm p-8 bg-black/40 backdrop-blur-sm"
      data-testid={`${testIdPrefix}-category-${slug}`}
    >
      <h4 className="heading-font text-2xl font-semibold text-[#e5e5e5] mb-6 tracking-tight">
        {category}
      </h4>
      <div className="space-y-5">
        {items.map((item) => (
          <div
            key={item.name}
            className="border-b border-[#2a2a2a] last:border-b-0 pb-4 last:pb-0"
            data-testid={`${testIdPrefix}-item-${item.name.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
          >
            <div className="flex justify-between items-baseline gap-3 mb-1">
              <span className="text-[#e5e5e5] font-medium">{item.name}</span>
              <span className="text-[#9ca3af] font-medium whitespace-nowrap">{item.price}</span>
            </div>
            {item.description && (
              <p className="text-sm text-[#a1a1a1] font-light leading-relaxed mt-1">
                {item.description}
              </p>
            )}
          </div>
        ))}
      </div>
    </motion.div>
  );
};

const BreakfastSection = () => (
  <section id="breakfast" className="py-32 px-6" data-testid="breakfast-section">
    <div className="max-w-7xl mx-auto">
      <motion.div
        initial={FADE_IN_UP.initial}
        whileInView={FADE_IN_UP.whileInView}
        viewport={VIEWPORT_ONCE}
        transition={TRANSITION_07}
        className="text-center mb-16"
      >
        <h3 className="heading-font text-5xl md:text-6xl font-semibold text-[#e5e5e5] tracking-tight mb-4">
          Breakfast Menu
        </h3>
        <p className="text-lg text-[#a1a1a1] font-light max-w-2xl mx-auto">
          Start your day with our freshly prepared breakfast favorites
        </p>
      </motion.div>

      <div className="grid md:grid-cols-2 gap-8">
        {Object.entries(breakfastMenu).map(([category, items], categoryIndex) => (
          <SimpleMenuCard
            key={category}
            testIdPrefix="breakfast"
            category={category}
            items={items}
            categoryIndex={categoryIndex}
          />
        ))}
      </div>
    </div>
  </section>
);

const LunchSection = () => (
  <section id="lunch" className="py-32 px-6" data-testid="lunch-section">
    <div className="max-w-7xl mx-auto">
      <motion.div
        initial={FADE_IN_UP.initial}
        whileInView={FADE_IN_UP.whileInView}
        viewport={VIEWPORT_ONCE}
        transition={TRANSITION_07}
        className="text-center mb-16"
      >
        <p className="text-xs text-[#9ca3af] uppercase tracking-[0.3em] mb-4 font-medium">Midday Bites</p>
        <h3 className="heading-font text-5xl md:text-6xl font-semibold text-[#e5e5e5] tracking-tight mb-4">
          Lunch Menu
        </h3>
        <p className="text-lg text-[#a1a1a1] font-light max-w-2xl mx-auto">
          Caribbean-inspired flavors and classic favorites, served fresh
        </p>
      </motion.div>

      <div className="grid md:grid-cols-2 gap-8">
        {Object.entries(lunchMenu).map(([category, items], categoryIndex) => (
          <SimpleMenuCard
            key={category}
            testIdPrefix="lunch"
            category={category}
            items={items}
            categoryIndex={categoryIndex}
          />
        ))}
      </div>

      <motion.p
        initial={OPACITY_FADE_INITIAL}
        whileInView={OPACITY_FADE_IN_VIEW}
        viewport={VIEWPORT_ONCE}
        transition={FOOTER_NOTE_TRANSITION}
        className="text-center text-sm text-[#a1a1a1] font-light mt-12 italic"
      >
        Sandwiches served with choice of Mixed Greens, Caesar, French Fries, Cole Slaw, or Fruit. <br />
        Pizza additions available: Grilled Chicken +$10, Shrimp +$12, Steak +$16, Salmon +$14, Mahi +$11
      </motion.p>
    </div>
  </section>
);

const WineCategoryCard = ({ category, items, categoryIndex }) => {
  const hasGlass = items.some((item) => !item.subsection && item.glass);
  const slug = category.toLowerCase().replace(/[^a-z0-9]+/g, '-');
  return (
    <motion.div
      initial={FADE_IN_UP.initial}
      whileInView={FADE_IN_UP.whileInView}
      viewport={VIEWPORT_ONCE}
      transition={getCardTransition(categoryIndex)}
      className="border border-[#3D2817]/60 rounded-sm p-8 bg-black/40 backdrop-blur-sm"
      data-testid={`wine-category-${slug}`}
    >
      <h4 className="heading-font text-2xl font-semibold text-[#e5e5e5] mb-2 tracking-tight">
        {category}
      </h4>
      <div className="grid grid-cols-[1fr,auto,auto] gap-3 pb-2 border-b border-[#333333] mb-4">
        <span></span>
        <span className="text-[#9ca3af] text-xs font-medium uppercase tracking-wide text-center w-14">{hasGlass ? "Glass" : ""}</span>
        <span className="text-[#9ca3af] text-xs font-medium uppercase tracking-wide text-center w-14">Bottle</span>
      </div>
      <div className="space-y-3">
        {items.map((item) => (
          item.subsection ? (
            <div
              key={`subsection-${item.subsection}`}
              className="pt-4 pb-2 mt-2 border-t border-[#333333]"
              data-testid={`wine-subsection-${item.subsection.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
            >
              <p className="text-sm text-white uppercase tracking-[0.2em] font-bold">
                {item.subsection}
              </p>
            </div>
          ) : (
            <div
              key={item.name}
              className="grid grid-cols-[1fr,auto,auto] gap-3 items-center py-1"
              data-testid={`wine-item-${item.name.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
            >
              <span className="text-[#d1d5db] font-normal text-sm">{item.name}</span>
              <span className="text-[#9ca3af] font-medium text-center w-14 text-sm">{item.glass}</span>
              <span className="text-[#9ca3af] font-medium text-center w-14 text-sm">{item.bottle}</span>
            </div>
          )
        ))}
      </div>
    </motion.div>
  );
};

const WineSection = () => (
  <section id="wine" className="py-32 px-6" data-testid="wine-section">
    <div className="max-w-7xl mx-auto">
      <motion.div
        initial={FADE_IN_UP.initial}
        whileInView={FADE_IN_UP.whileInView}
        viewport={VIEWPORT_ONCE}
        transition={TRANSITION_07}
        className="text-center mb-16"
      >
        <p className="text-xs text-[#9ca3af] uppercase tracking-[0.3em] mb-4 font-medium">Curated Selection</p>
        <h3 className="heading-font text-5xl md:text-6xl font-semibold text-[#e5e5e5] tracking-tight mb-4">
          Wine List
        </h3>
        <p className="text-lg text-[#a1a1a1] font-light max-w-2xl mx-auto">
          An elegant selection of international wines, served by the glass or bottle
        </p>
      </motion.div>

      <div className="grid md:grid-cols-2 gap-8">
        {Object.entries(wineMenu).map(([category, items], categoryIndex) => (
          <WineCategoryCard
            key={category}
            category={category}
            items={items}
            categoryIndex={categoryIndex}
          />
        ))}
      </div>

      <motion.p
        initial={OPACITY_FADE_INITIAL}
        whileInView={OPACITY_FADE_IN_VIEW}
        viewport={VIEWPORT_ONCE}
        transition={FOOTER_NOTE_TRANSITION}
        className="text-center text-sm text-[#a1a1a1] font-light mt-12 italic"
        data-testid="wine-premium-note"
      >
        (An asterisk <span className="text-[#d1b89f] not-italic font-medium">*</span> next to a wine indicates it is a premium wine)
      </motion.p>
    </div>
  </section>
);

const LocationSection = () => (
  <section id="location" className="py-24 px-6" data-testid="location-section">
    <div className="max-w-4xl mx-auto text-center">
      <motion.div
        initial={FADE_IN_UP.initial}
        whileInView={FADE_IN_UP.whileInView}
        viewport={VIEWPORT_ONCE}
        transition={TRANSITION_07}
      >
        <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-[#A47551] mb-6">
          <MapPin className="w-8 h-8 text-[#1a0f0a]" />
        </div>
        <h3 className="heading-font text-4xl md:text-5xl font-semibold text-[#e5e5e5] tracking-tight mb-4">
          Visit Us
        </h3>
        <p className="text-xl text-[#e5e5e5] mb-2 font-normal" data-testid="location-address">
          Tutu Park Mall, Suite 181
        </p>
        <p className="text-xl text-[#a1a1a1] mb-8 font-light">
          St. Thomas, VI 00802
        </p>

        {/* Business Hours */}
        <div className="bg-black/40 backdrop-blur-sm border border-[#3D2817]/60 rounded-lg p-8 max-w-md mx-auto mb-8" data-testid="business-hours">
          <h4 className="heading-font text-2xl font-semibold text-[#e5e5e5] mb-6">Hours of Operation</h4>
          <div className="space-y-3">
            <div className="flex justify-between items-center">
              <span className="text-[#d1d5db] font-normal">Monday - Saturday</span>
              <span className="text-[#9ca3af] font-medium">7:30am - 7:00pm</span>
            </div>
            <div className="flex justify-between items-center pt-3 border-t border-[#333333]">
              <span className="text-[#d1d5db] font-normal">Sunday</span>
              <span className="text-[#9ca3af] font-medium">Closed</span>
            </div>
          </div>
        </div>

        {/* Contact Information */}
        <div className="flex flex-col sm:flex-row gap-6 justify-center items-center mb-8 flex-wrap" data-testid="contact-info">
          <a
            href="tel:+13407257023"
            className="flex items-center gap-3 text-[#d1d5db] hover:text-[#e5e5e5] transition-colors duration-300"
            data-testid="contact-phone"
          >
            <div className="w-10 h-10 rounded-full bg-black/40 backdrop-blur-sm border border-[#3D2817]/60 flex items-center justify-center">
              <Phone className="w-5 h-5 text-[#9ca3af]" />
            </div>
            <span className="font-medium">(340) 725-7023</span>
          </a>
          <a
            href="mailto:info@leposhecafe.com"
            className="flex items-center gap-3 text-[#d1d5db] hover:text-[#e5e5e5] transition-colors duration-300"
            data-testid="contact-email"
          >
            <div className="w-10 h-10 rounded-full bg-black/40 backdrop-blur-sm border border-[#3D2817]/60 flex items-center justify-center">
              <Mail className="w-5 h-5 text-[#9ca3af]" />
            </div>
            <span className="font-medium">info@leposhecafe.com</span>
          </a>
          <a
            href="https://www.leposhecafe.com"
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-3 text-[#d1d5db] hover:text-[#e5e5e5] transition-colors duration-300"
            data-testid="contact-website"
          >
            <div className="w-10 h-10 rounded-full bg-black/40 backdrop-blur-sm border border-[#3D2817]/60 flex items-center justify-center">
              <Globe className="w-5 h-5 text-[#9ca3af]" />
            </div>
            <span className="font-medium">www.leposhecafe.com</span>
          </a>
        </div>

        <p className="text-lg text-[#a1a1a1] font-light max-w-2xl mx-auto mb-12">
          Stop by and experience the warmth of Le Poshe Cafe. We can&apos;t wait to serve you!
        </p>

        {/* Find Us - Google Maps Embed */}
        <div className="max-w-4xl mx-auto" data-testid="find-us-map">
          <h4 className="heading-font text-3xl font-semibold text-[#e5e5e5] mb-6">Find Us</h4>
          <div className="rounded-lg overflow-hidden border border-[#3D2817]/60 shadow-2xl">
            <iframe
              src="https://maps.google.com/maps?q=Tutu+Park+Mall,+St+Thomas,+VI+00802&t=&z=15&ie=UTF8&iwloc=&output=embed"
              width="100%"
              height="400"
              style={{ border: 0, filter: "invert(0.9) hue-rotate(180deg)" }}
              allowFullScreen=""
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              title="Le Poshe Cafe Location - Tutu Park Mall"
              data-testid="google-maps-iframe"
            ></iframe>
          </div>
          <a
            href="https://www.google.com/maps/search/?api=1&query=Tutu+Park+Mall+Suite+181+St+Thomas+VI+00802"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-block mt-6 bg-[#A47551] text-[#1a0f0a] px-8 py-3 rounded-full text-sm uppercase tracking-wide font-medium hover:bg-[#C99072] transition-colors duration-300"
            data-testid="get-directions-button"
          >
            Get Directions
          </a>
        </div>
      </motion.div>
    </div>
  </section>
);

const SiteFooter = () => (
  <footer className="py-12 px-6 border-t border-[#3D2817]/60" data-testid="footer">
    <div className="max-w-7xl mx-auto text-center">
      <div className="flex flex-col items-center justify-center mb-4">
        <img
          src="https://customer-assets.emergentagent.com/job_image-based-web/artifacts/4c50li41_20260707_184349_0000.png"
          alt="Le Poshe Cafe"
          className="h-24 md:h-28 w-auto object-contain rounded-sm mb-3"
        />
        <p className="text-sm text-[#d1b89f] italic tracking-wide">
          Brewed Awakenings, Baked Dreams
        </p>
      </div>
      <p className="text-sm text-[#a1a1a1] font-light mt-4">
        <a href="https://www.leposhecafe.com" target="_blank" rel="noopener noreferrer" className="hover:text-[#d1b89f] transition-colors duration-300">
          www.leposhecafe.com
        </a>
      </p>
      <p className="text-sm text-[#a1a1a1] font-light mt-2">
        © 2026 Le Poshe Cafe. All rights reserved.
      </p>
    </div>
  </footer>
);


function App() {
  const [activeSection, setActiveSection] = useState("home");
  const [favorites, setFavorites] = useState([]);
  const [selectedItem, setSelectedItem] = useState(null);

  const toggleFavorite = useCallback((category, itemName) => {
    const key = `${category}-${itemName}`;
    setFavorites((prev) =>
      prev.includes(key) ? prev.filter((f) => f !== key) : [...prev, key]
    );
  }, []);

  const isFavorite = useCallback((category, itemName) => {
    return favorites.includes(`${category}-${itemName}`);
  }, [favorites]);

  useEffect(() => {
    const handleScroll = () => {
      const scrollPosition = window.scrollY + 100;

      for (const section of NAV_SECTIONS) {
        const element = document.getElementById(section);
        if (element) {
          const { offsetTop, offsetHeight } = element;
          if (scrollPosition >= offsetTop && scrollPosition < offsetTop + offsetHeight) {
            setActiveSection(section);
            break;
          }
        }
      }
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  return (
    <div className="min-h-screen">
      {/* Navigation */}
      <motion.nav
        initial={NAV_INITIAL}
        animate={NAV_ANIMATE}
        transition={NAV_TRANSITION}
        className="fixed top-0 left-0 right-0 z-50 bg-gradient-to-b from-[#2A1810] via-[#2A1810]/95 to-[#2A1810]/85 backdrop-blur-sm"
      >
        <div className="max-w-7xl mx-auto px-6 py-4 flex justify-between items-center">
          <a href="#home" className="flex flex-col items-start" data-testid="nav-logo">
            <img
              src="https://customer-assets.emergentagent.com/job_image-based-web/artifacts/4c50li41_20260707_184349_0000.png"
              alt="Le Poshe Cafe"
              className="h-20 md:h-24 w-auto object-contain rounded-sm"
            />
            <p className="text-[11px] md:text-xs text-[#d1b89f] italic tracking-wide leading-tight mt-1 ml-1">
              Brewed Awakenings, Baked Dreams
            </p>
          </a>
          <div className="hidden md:flex gap-8" data-testid="nav-menu">
            {NAV_SECTIONS.map((section) => (
              <a
                key={section}
                href={`#${section}`}
                data-testid={`nav-link-${section}`}
                className={`nav-link text-base uppercase tracking-wide font-semibold ${
                  activeSection === section ? "text-[#d1b89f]" : "text-[#e5e5e5]"
                }`}
              >
                {section}
              </a>
            ))}
            {favorites.length > 0 && (
              <div className="flex items-center gap-2 text-[#d1b89f]" data-testid="favorites-counter">
                <Heart className="w-5 h-5 fill-[#d1b89f]" />
                <span className="text-base font-semibold">{favorites.length}</span>
              </div>
            )}
          </div>
        </div>
      </motion.nav>

      <HeroSection />

      <AboutSection />

      {/* Menu Section */}
      <section id="menu" className="py-32 px-6" data-testid="menu-section">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={FADE_IN_UP.initial}
            whileInView={FADE_IN_UP.whileInView}
            viewport={VIEWPORT_ONCE}
            transition={TRANSITION_07}
            className="text-center mb-16"
          >
            <h3 className="heading-font text-5xl md:text-6xl font-semibold text-[#e5e5e5] tracking-tight mb-4">
              Our Menu
            </h3>
            <p className="text-lg text-[#a1a1a1] font-light max-w-3xl mx-auto leading-relaxed">
              Explore our full selection of handcrafted beverages, an assortment of fresh pastries 
              <span className="text-[#9ca3af] italic"> (ask your server)</span>, hearty breakfast, 
              flavorful lunch, and a curated wine selection — all crafted to delight your senses.
            </p>
          </motion.div>

          {/* Menu Grid */}
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {Object.entries(menuData).map(([category, items], categoryIndex) => (
              <BeverageMenuCard
                key={category}
                category={category}
                items={items}
                categoryIndex={categoryIndex}
                onSelectItem={setSelectedItem}
                toggleFavorite={toggleFavorite}
                isFavorite={isFavorite}
              />
            ))}
          </div>
        </div>
      </section>

      {/* Breakfast Section */}
      <BreakfastSection />

      {/* Lunch Section */}
      <LunchSection />

      {/* Wine Section */}
      <WineSection />

      {/* Item Detail Modal */}
      <AnimatePresence>
        <ItemModal
          item={selectedItem}
          onClose={() => setSelectedItem(null)}
          toggleFavorite={toggleFavorite}
          isFavorite={isFavorite}
        />
      </AnimatePresence>

      {/* Location Section */}
      <LocationSection />

      {/* Footer */}
      <SiteFooter />
    </div>
  );
}

export default App;